import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const VerifyOTP = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Get email from location state or localStorage
  useEffect(() => {
    const userEmail = location.state?.email || localStorage.getItem('pendingVerificationEmail');
    if (userEmail) {
      setEmail(userEmail);
      localStorage.setItem('pendingVerificationEmail', userEmail);
    } else {
      // If no email is found, redirect to login
      navigate('/login');
    }
  }, [location.state, navigate]);

  // Handle countdown for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Por favor ingrese un código de verificación válido de 6 dígitos');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Verify the code
      const response = await axios.post('/api/auth/verify-code', {
        email,
        code: verificationCode
      });
      
      setSuccess(true);
      
      // After successful verification, attempt to log in
      setTimeout(async () => {
        // We need to get the password from the user again for security reasons
        navigate('/login', { 
          state: { 
            verified: true, 
            message: '¡Correo electrónico verificado exitosamente! Por favor inicie sesión.' 
          } 
        });
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Verificación fallida. Por favor revise su código e intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    
    try {
      await axios.post('/api/auth/resend-verification', { email });
      setCountdown(60); // Disable resend button for 60 seconds
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al reenviar el código de verificación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-form-container">
        <div className="auth-header">
          <h2>Verifique su correo electrónico</h2>
          <p>Hemos enviado un código de verificación a <strong>{email}</strong></p>
        </div>
        
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}
        
        {success && (
          <div className="success-message">
            <i className="fas fa-check-circle"></i> ¡Verificación exitosa! Redirigiendo...
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="verification-code">Código de verificación</label>
            <input
              id="verification-code"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').substring(0, 6))}
              disabled={loading || success}
              placeholder="Ingrese código de 6 dígitos"
              maxLength="6"
              required
              autoFocus
              className="verification-code-input"
            />
          </div>
          
          <button 
            type="submit" 
            className="btn-primary" 
            disabled={loading || success || verificationCode.length !== 6}
          >
            {loading ? (
              <>
                <span className="spinner"></span> Verificando...
              </>
            ) : (
              'Verificar correo'
            )}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>
            ¿No recibió el código?{' '}
            {countdown > 0 ? (
              <span>Reenviar en {countdown}s</span>
            ) : (
              <button 
                onClick={handleResendCode} 
                className="btn-link"
                disabled={loading || countdown > 0}
              >
                Reenviar código
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP;
